import React, { createContext, useState, useEffect, useContext } from 'react';
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  orderBy, 
  limit, 
  doc, 
  updateDoc, 
  setDoc,
  getDoc,
  serverTimestamp,
  getDocs
} from 'firebase/firestore';
import { db, sendWebNotification } from '../config/firebase';

// Create the context
export const NotificationContext = createContext();

// Default user ID for non-authenticated application
const defaultUserId = 'default-user-id';

// Create a provider component
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize notifications
  useEffect(() => {
    const initializeNotifications = async () => {
      // Request notification permission
      try {
        // Check browser support and request permission 
        if ('Notification' in window) {
          const permission = await Notification.requestPermission();
          setNotificationsEnabled(permission === 'granted');
        }
      } catch (error) {
        console.error('Error requesting notification permission:', error);
      }
      
      setIsInitialized(true);
    };

    initializeNotifications();
  }, []);

  // Use a polling approach instead of listeners to avoid Firestore index issues
  useEffect(() => {
    if (!isInitialized) return;

    // Set up polling interval to fetch notifications
    const fetchNotifications = async () => {
      try {
        const conversationsCol = collection(db, 'conversations');
        const snapshot = await getDocs(conversationsCol);
        
        let newNotifications = [];
        let totalUnreadCount = 0;
        
        snapshot.forEach(docSnapshot => {
          const data = docSnapshot.data();
          
          // Check for user role
          if (data.userId === defaultUserId && data.userUnreadCount > 0) {
            totalUnreadCount += data.userUnreadCount;
            
            newNotifications.push({
              id: docSnapshot.id,
              title: `Nuevo mensaje de ${data.providerName}`,
              body: data.lastMessage,
              timestamp: data.lastMessageTimestamp?.toDate() || new Date(),
              conversationId: docSnapshot.id,
              type: data.lastMessage.includes('reserva') ? 'booking' : 'message',
              isRead: false,
              role: 'user'
            });
          }
          
          // Check for provider role
          if (data.providerId === defaultUserId && data.providerUnreadCount > 0) {
            totalUnreadCount += data.providerUnreadCount;
            
            newNotifications.push({
              id: docSnapshot.id,
              title: `Nuevo mensaje de ${data.userName}`,
              body: data.lastMessage,
              timestamp: data.lastMessageTimestamp?.toDate() || new Date(),
              conversationId: docSnapshot.id,
              type: data.lastMessage.includes('reserva') ? 'booking' : 'message',
              isRead: false,
              role: 'provider'
            });
          }
        });
        
        // Sort by timestamp
        newNotifications.sort((a, b) => b.timestamp - a.timestamp);
        
        setNotifications(newNotifications);
        setUnreadCount(totalUnreadCount);
        
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };

    // Fetch initially
    fetchNotifications();
    
    // Set up interval to poll every 10 seconds
    const intervalId = setInterval(fetchNotifications, 10000);
    
    return () => {
      clearInterval(intervalId);
    };
  }, [isInitialized]);

  // Mark a notification as read
  const markAsRead = async (notificationId) => {
    try {
      // Find the notification
      const notification = notifications.find(n => n.id === notificationId);
      if (!notification) return;

      // Update the local state
      setNotifications(prev => prev.map(item => {
        if (item.id === notificationId) {
          return { ...item, isRead: true };
        }
        return item;
      }));
      
      // Reduce the unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      // If it's a conversation notification, update Firestore based on role
      if (notification.conversationId) {
        const conversationRef = doc(db, 'conversations', notification.conversationId);
        
        // Determine which field to update based on the role
        if (notification.role === 'user') {
          await updateDoc(conversationRef, { userUnreadCount: 0 });
        } else {
          await updateDoc(conversationRef, { providerUnreadCount: 0 });
        }
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!notifications.length) return;
    
    try {
      // Update local state
      setNotifications(prev => prev.map(item => ({ ...item, isRead: true })));
      setUnreadCount(0);
      
      // Group notifications by role to update conversations
      const userConversations = notifications
        .filter(n => n.role === 'user' && n.conversationId)
        .map(n => n.conversationId);
        
      const providerConversations = notifications
        .filter(n => n.role === 'provider' && n.conversationId)
        .map(n => n.conversationId);
      
      // Update user conversations
      for (const conversationId of userConversations) {
        await updateDoc(doc(db, 'conversations', conversationId), {
          userUnreadCount: 0
        });
      }
      
      // Update provider conversations
      for (const conversationId of providerConversations) {
        await updateDoc(doc(db, 'conversations', conversationId), {
          providerUnreadCount: 0
        });
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Clear all notifications from UI (doesn't affect database)
  const clearNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  // The context value
  const value = {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearNotifications,
    notificationsEnabled
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook to use the notification context
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}; 